import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Box, Collapse, Dialog, DialogContent, DialogTitle, IconButton, Tooltip, Typography } from "@mui/material";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CloseIcon from "@mui/icons-material/Close";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import SendIcon from "@mui/icons-material/Send";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TagOutlinedIcon from "@mui/icons-material/TagOutlined";
import i18next from "i18next";
import { BRAND, FONT, RADIUS, SHADOW, useLightStyles, DOSSIER_STATUS, hexToRgba } from "../lightTheme";
import { getEnv } from "config/Config";
import { getUrlWithAccountId } from "config/Routes";
import { Context } from "states/Store";
import { formatDt, normColor, transformDocuments } from "../utils/lightUtils";
import { useThirdpartyDetailStyles } from "../LightThirdpartyDetail";
import StopConfirmDialog from "./StopConfirmDialog";
import DocumentRow from "./DocumentRow";
import LightNotesList from "./LightNotesList";
import useMentionAutocomplete from "../hooks/useMentionAutocomplete";
import LightLoader from "../LightLoader";
import LightDocViewer from "../LightDocViewer";
import LightEditableAttributes from "../LightEditableAttributes";
import { getDoctypeTranslatedName } from "services/common/DocumentUtils";
import { getDossierAttributes } from "services/edge/AttributesService";
import { getDossierIndicators, getDossierSheet, stopDossier, reactivateDossier, updateRequiredDocuments } from "services/edge/DossierService";
import { getAccountRequirements } from "services/edge/AccountService";
import { isSealedDocument } from "services/common/Constants";
import { getAllNoteDossier, createNoteDossier, deleteNoteDossier } from "services/edge/NoteService";
import { getAccountUsers } from "services/edge/UserService";
import { formatCalculationResult } from "services/common/CalculatorUtils";
import { getApiErrorMessageCode } from "services/common/ApiUtils";
import { ERROR, SUCCESS } from "services/common/Constants";

export default function DossierAccordion({ dossier, indicators: initIndicators, accountId, dispatch, onStopped, onChanged, readOnly, fromContextId, onReactivated, hasDossierAttrDefs, reopenDoc, onDocReopened, contextName }) {
  const { classes: s } = useThirdpartyDetailStyles();
  const { classes: light } = useLightStyles();
  const [state] = useContext(Context);
  const history = useHistory();

  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);

  // Dossier detail data
  const [dossierDetail, setDossierDetail] = useState(null);
  const [dossierAttrs, setDossierAttrs] = useState([]);
  const [indicators, setIndicators] = useState(initIndicators ?? []);
  const [notes, setNotes] = useState([]);
  const [accountUsers, setAccountUsers] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [viewerDoc, setViewerDoc] = useState(null);
  const [editAttrsOpen, setEditAttrsOpen] = useState(false);
  const mention = useMentionAutocomplete(accountUsers, noteText, setNoteText);

  // Stop dossier
  const [stopDialogOpen, setStopDialogOpen] = useState(false);
  const [stopReason, setStopReason] = useState("");
  const [stopping, setStopping] = useState(false);

  // Add document
  const [addDocOpen, setAddDocOpen] = useState(false);
  const [accountReqs, setAccountReqs] = useState(null);
  const [selectedNewDocs, setSelectedNewDocs] = useState([]);
  const [addingDocs, setAddingDocs] = useState(false);
  const [docFilter, setDocFilter] = useState("");

  // Reactivate dossier
  const [reactivatingDossier, setReactivatingDossier] = useState(false);
  const handleReactivateDossier = async () => {
    setReactivatingDossier(true);
    try {
      await reactivateDossier(accountId, dossier.dossierId, dossier, dispatch);
      dispatch({ type: "ALERT", alert: { type: SUCCESS, msg: i18next.t("light.detail.reactivated") } });
      if (onReactivated) onReactivated(dossier.dossierId);
    } catch (e) {
      dispatch({ type: "ALERT", alert: { type: ERROR, msg: getApiErrorMessageCode(e) } });
    } finally {
      setReactivatingDossier(false);
    }
  };

  const loadDetail = async (force = false, silent = false) => {
    if (!force && (loaded || loading)) return;
    if (!silent) setLoading(true);
    try {
      const [sheetRes, notesRes] = await Promise.all([
        getDossierSheet(accountId, dossier.dossierId, i18next.language, dispatch),
        getAllNoteDossier(accountId, dossier.dossierId, null),
      ]);
      setDossierDetail(sheetRes?.content ?? null);
      setNotes(notesRes?.content ?? []);

      await Promise.allSettled([
        getDossierAttributes(accountId, dossier.dossierId, null)
          .then((r) => setDossierAttrs((r?.content ?? []).filter((a) => a.values?.length > 0))),
        getDossierIndicators(accountId, dossier.dossierId, dispatch)
          .then((r) => { if (r?.content?.length) setIndicators(r.content); }),
        getAccountUsers(accountId, dispatch)
          .then((r) => setAccountUsers(r?.content ?? [])),
      ]);
    } catch (_) { /* silent */ }
    if (!silent) setLoading(false);
    setLoaded(true);
  };

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !loaded) loadDetail();
  };

  useEffect(() => {
    if (reopenDoc && !open) {
      setOpen(true);
      if (!loaded) loadDetail();
    }
  }, [reopenDoc]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (reopenDoc && dossierDetail) {
      const docs = (dossierDetail.documents ?? []);
      const match = docs.find((d) => {
        const ev = [].concat(d.evidences ?? [], (d.historicEvidences ?? []).filter((e) => e.statusReason !== "ARRET_DEMANDE"));
        const rId = ev[0]?.responseId;
        return (reopenDoc.reponseId && rId === reopenDoc.reponseId) || (reopenDoc.typeDocumentId && d.typeDocumentId === reopenDoc.typeDocumentId);
      });
      if (match) {
        const ev = [].concat(match.evidences ?? [], (match.historicEvidences ?? []).filter((e) => e.statusReason !== "ARRET_DEMANDE"));
        setViewerDoc({ ...match, reponseId: ev[0]?.responseId, fileId: ev[0]?.id, evidences: ev, canOpen: true });
      } else {
        setViewerDoc(reopenDoc);
      }
      if (onDocReopened) onDocReopened();
    }
  }, [dossierDetail]); // eslint-disable-line react-hooks/exhaustive-deps

  const documents = transformDocuments(dossierDetail?.documents, state.doctypeTraductions, state.lang);

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setSavingNote(true);
    try {
      const mentionRegexp = /@([\w\u00C0-\u017E]+ [\w\u00C0-\u017E]+)/g;
      const matched = [...noteText.matchAll(mentionRegexp)].map((m) => m[1]);
      const filteredUsers = mention.selectedMentions
        .filter((m) => matched.includes(m.nomPrenom))
        .map((m) => m.userOid);
      await createNoteDossier(accountId, dossier.dossierId, {
        content: noteText.trim(),
        selectedUsersIds: filteredUsers,
      }, dispatch);
      setNoteText("");
      mention.resetMentions();
    } catch (_) { /* silent */ }
    setSavingNote(false);
    try {
      const res = await getAllNoteDossier(accountId, dossier.dossierId, null);
      setNotes(res?.content ?? []);
    } catch (_) { /* silent */ }
  };

  const handleDeleteNote = async (noteOId) => {
    try {
      await deleteNoteDossier(accountId, dossier.dossierId, noteOId, dispatch);
      setNotes((prev) => prev.filter((n) => n.noteOId !== noteOId));
    } catch (_) { /* silent */ }
  };

  const handleStopDossier = async () => {
    setStopping(true);
    try {
      await stopDossier(accountId, dossier.dossierId, stopReason.trim(), dispatch);
      dispatch({ type: "ALERT", alert: { type: SUCCESS, msg: i18next.t("light.detail.stopped") } });
      setStopDialogOpen(false);
      setStopReason("");
      if (onStopped) onStopped(dossier.dossierId);
    } catch (e) {
      dispatch({ type: "ALERT", alert: { type: ERROR, msg: getApiErrorMessageCode(e) } });
    } finally {
      setStopping(false);
    }
  };

  const handleOpenAddDoc = async () => {
    setAddDocOpen(true);
    setDocFilter("");
    setSelectedNewDocs(documents.map((d) => d.documentCode));
    if (accountReqs === null) {
      try {
        const res = await getAccountRequirements(accountId, dispatch);
        setAccountReqs(res?.content ?? []);
      } catch (_) { setAccountReqs([]); }
    }
  };

  const handleConfirmDocs = async () => {
    setAddingDocs(true);
    try {
      await updateRequiredDocuments(accountId, dossier.dossierId, { newRequiredDocuments: selectedNewDocs }, dispatch);
      const sheetRes = await getDossierSheet(accountId, dossier.dossierId, i18next.language, dispatch);
      setDossierDetail(sheetRes?.content ?? null);
      dispatch({ type: "ALERT", alert: { type: SUCCESS, msg: i18next.t("light.detail.docsUpdated") } });
      setAddDocOpen(false);
      if (onChanged) onChanged(dossier.dossierId, sheetRes?.content?.statusCode);
    } catch (e) {
      dispatch({ type: "ALERT", alert: { type: ERROR, msg: getApiErrorMessageCode(e) } });
    } finally {
      setAddingDocs(false);
    }
  };

  const isLocked = dossierDetail?.dossierTypeLock === true || dossier.dossierTypeLock === true;
  const isStopped = Boolean(dossier.stoppingDate);
  const stCfg = DOSSIER_STATUS[dossier.statusCode] ?? { label: dossier.statusCode, bg: BRAND.background, color: BRAND.textMuted };
  const dIndics = indicators ?? [];

  return (
    <>
    <Box
      style={{ borderRadius: RADIUS.md, border: `1px solid ${open ? BRAND.primary : BRAND.border}`, backgroundColor: BRAND.white, overflow: "hidden", transition: "border-color 0.15s, box-shadow 0.15s, transform 0.15s", boxShadow: open ? SHADOW.soft : "none" }}
      onMouseEnter={(e) => { if (!open) { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,58,34,0.10)"; e.currentTarget.style.borderColor = BRAND.primary; } }}
      onMouseLeave={(e) => { if (!open) { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.borderColor = BRAND.border; } }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: { xs: "0.6rem", sm: "1rem" }, padding: "0.75rem 1rem", cursor: "pointer", flexWrap: { xs: "wrap", sm: "nowrap" } }} onClick={handleToggle}>
        <Box className={s.dossierIcon} style={{ backgroundColor: `${stCfg.color}15` }}>
          <FolderOutlinedIcon style={{ fontSize: "16px", color: stCfg.color }} />
        </Box>
        <Box className={s.dossierInfo} sx={{ flexBasis: { xs: "calc(100% - 56px)", sm: "auto" } }}>
          <Typography className={s.dossierName}>{dossier.dossierTypeLibelle || i18next.t("light.common.freeEvaluation")}</Typography>
          <Typography className={s.dossierMeta}>
            {formatDt(dossier.creationDate)}
            {contextName && (
              <span style={{ marginLeft: "0.5rem", display: "inline-flex", alignItems: "center", gap: "0.2rem", backgroundColor: `${BRAND.primary}08`, border: `1px solid ${BRAND.primary}18`, borderRadius: "4px", padding: "1px 6px", fontSize: "0.65rem", fontWeight: 600, color: BRAND.primary, fontFamily: FONT.family, verticalAlign: "middle" }}>
                {contextName}
              </span>
            )}
          </Typography>
        </Box>
        <Box component="span" className={s.dossierStatus} sx={{ backgroundColor: hexToRgba(stCfg.color, 0.18), color: stCfg.color, order: { xs: 3, sm: 0 } }}>{stCfg.label}</Box>
        {readOnly && (
          <Tooltip title={i18next.t("light.contexts.dossierDetail")} placement="top" disableInteractive>
            <Box
              component="a"
              href={getUrlWithAccountId(accountId, `light/dossiers/${dossier.dossierId}`)}
              onClick={(e) => { e.stopPropagation(); if (e.metaKey || e.ctrlKey) return; e.preventDefault(); history.push({ pathname: getUrlWithAccountId(accountId, `light/dossiers/${dossier.dossierId}`), state: { fromContextId } }); }}
              style={{ display: "flex", alignItems: "center", gap: "0.25rem", ...FONT.meta, fontWeight: 600, color: BRAND.primary, fontFamily: FONT.family, textDecoration: "none", padding: "4px 8px", borderRadius: RADIUS.sm, border: `1px solid ${BRAND.border}`, backgroundColor: BRAND.white, whiteSpace: "nowrap", flexShrink: 0 }}
            >
              <FolderOutlinedIcon style={{ fontSize: "13px" }} />
              {i18next.t("light.contexts.dossierDetail")}
            </Box>
          </Tooltip>
        )}
        {!isStopped && !readOnly && (
          <Tooltip title={i18next.t("light.detail.stopEvalTooltip")} placement="top" disableInteractive>
            <Box
              component="button"
              className={s.docActionBtn}
              onClick={(e) => { e.stopPropagation(); setStopDialogOpen(true); }}
              sx={{
                color: BRAND.textMuted, opacity: 0.5,
                display: "flex", alignItems: "center", justifyContent: "center",
                alignSelf: "center",
                ml: { xs: "auto", sm: 0 },
                order: { xs: 5, sm: 0 },
              }}
            >
              <StopCircleOutlinedIcon style={{ fontSize: "18px" }} />
            </Box>
          </Tooltip>
        )}
        {isStopped && onReactivated && (
          <Tooltip title={i18next.t("light.detail.reactivateEvalTooltip")} placement="top" disableInteractive>
            <Box
              component="button"
              className={s.docActionBtn}
              onClick={(e) => { e.stopPropagation(); handleReactivateDossier(); }}
              disabled={reactivatingDossier}
              sx={{
                color: BRAND.primary, opacity: 0.6,
                display: "flex", alignItems: "center", justifyContent: "center",
                alignSelf: "center",
                ml: { xs: "auto", sm: 0 },
                order: { xs: 5, sm: 0 },
              }}
            >
              <PlayArrowIcon style={{ fontSize: "18px" }} />
            </Box>
          </Tooltip>
        )}
        <ExpandMoreIcon className={s.dossierChevron} style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }} />
      </Box>

      <Collapse in={open} timeout={250} unmountOnExit>
        <Box className={s.accordionBody}>
          {loading ? (
            <LightLoader size="sm" label={i18next.t("light.common.loading")} />
          ) : (
            <>
              <Box className={s.accordionSection}>
                <Box style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Box className={s.accordionSectionLabel}>
                    <ArticleOutlinedIcon style={{ fontSize: "13px" }} />
                    {i18next.t("light.detail.documents")}
                    {documents.length > 0 && <span style={{ ...FONT.meta, color: BRAND.textMuted, fontWeight: 400 }}>({documents.length})</span>}
                  </Box>
                  {!isLocked && !isStopped && !readOnly && (
                    <button
                      onClick={() => handleOpenAddDoc()}
                      style={{ display: "flex", alignItems: "center", gap: "0.25rem", border: "none", background: "transparent", cursor: "pointer", color: BRAND.primary, ...FONT.meta, fontFamily: FONT.family, fontWeight: 600, padding: "2px 4px", borderRadius: RADIUS.sm, transition: "opacity 0.15s" }}
                      onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.7"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
                    >
                      <EditOutlinedIcon style={{ fontSize: "12px" }} />
                      {i18next.t("light.detail.manageDocuments")}
                    </button>
                  )}
                </Box>
                {documents.length === 0 ? (
                  <span style={{ ...FONT.small, color: BRAND.textMuted, fontFamily: FONT.family, fontStyle: "italic" }}>{i18next.t("light.detail.noDoc")}</span>
                ) : (
                  <Box style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                    {documents.map((doc, i) => (
                      <DocumentRow
                        key={doc.documentCode ?? i}
                        doc={doc}
                        compact
                        showSeeAlways={false}
                        onView={(d) => { if (d.canOpen) setViewerDoc(d); }}
                        onRespond={(d) => {
                          sessionStorage.setItem("light_form_return", JSON.stringify({ returnUrl: window.location.pathname, reopenDoc: { reponseId: d.reponseId, fileId: d.fileId, documentName: d.documentName, typeDocumentId: d.typeDocumentId, acquisition: d.acquisition, dossierId: dossier.dossierId } }));
                          window.location.href = `${getEnv("REACT_APP_API_URL_FORM")}/compteDO/${accountId}/document/${d.typeDocumentId}/dossier/${dossier.dossierId}`;
                        }}
                        classes={s}
                      />
                    ))}
                  </Box>
                )}
              </Box>

              {dIndics.length > 0 && (
                <Box className={s.accordionSection}>
                  <Box className={s.accordionSectionLabel}>{i18next.t("light.detail.indicators")}</Box>
                  <Box style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
                    {dIndics.map((ind) => {
                      const color = normColor(ind.resultColorCode) || "#94A3B8";
                      const val = formatCalculationResult(ind);
                      const displayVal = val === true ? i18next.t("yes") : val === false ? i18next.t("no") : val;
                      if (!displayVal && displayVal !== 0) return null;
                      return (
                        <Tooltip key={ind.name} title={`${ind.label || ind.name} : ${displayVal}`} placement="top" enterDelay={200} disableInteractive>
                          <span className={light.indicatorTag} style={{ backgroundColor: `${color}12`, border: `1px solid ${color}25` }}>
                            <Box className={light.indicatorDot} style={{ backgroundColor: color }} />
                            <span className={light.indicatorLabel}>{ind.label || ind.name}</span>
                            <span className={light.indicatorValue} style={{ color }}>{displayVal}</span>
                          </span>
                        </Tooltip>
                      );
                    })}
                  </Box>
                </Box>
              )}

              {hasDossierAttrDefs && <Box className={s.accordionSection}>
                <Box style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                  <Box className={s.accordionSectionLabel}>
                    <TagOutlinedIcon style={{ fontSize: "13px" }} />
                    {i18next.t("light.detail.attributes")}
                  </Box>
                  {!readOnly && (
                    <button
                      onClick={() => setEditAttrsOpen(true)}
                      style={{ border: "none", background: "transparent", cursor: "pointer", color: BRAND.textMuted, padding: "2px", borderRadius: RADIUS.sm, transition: "color 0.15s", display: "flex", alignItems: "center" }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = BRAND.primary; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = BRAND.textMuted; }}
                    >
                      <EditOutlinedIcon style={{ fontSize: "14px" }} />
                    </button>
                  )}
                </Box>
                {dossierAttrs.length === 0 ? (
                  <span style={{ ...FONT.small, color: BRAND.textMuted, fontFamily: FONT.family, fontStyle: "italic" }}>{i18next.t("light.detail.noAttribute")}</span>
                ) : (
                  <Box style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
                    {dossierAttrs.map((a) => {
                      const vals = Array.isArray(a.values) ? a.values : [a.values];
                      return (
                        <Tooltip key={a.label} title={`${a.label} : ${vals.join(", ")}`} placement="top" enterDelay={200} disableInteractive>
                          <Box className={light.attrTag}>
                            <span className={light.attrTagLabel}>{a.label}</span>
                            <span className={light.attrTagValue}>{vals.join(", ")}</span>
                          </Box>
                        </Tooltip>
                      );
                    })}
                  </Box>
                )}
              </Box>}

              <Box className={s.accordionSection}>
                <Box className={s.accordionSectionLabel}>
                  <ChatBubbleOutlineIcon style={{ fontSize: "13px" }} />
                  {i18next.t("light.detail.notes")}
                  {notes.length > 0 && <span style={{ ...FONT.meta, color: BRAND.textMuted, fontWeight: 400 }}>({notes.length})</span>}
                </Box>
                {!readOnly && <Box className={s.noteInput}>
                  <Box className={s.mentionInputWrap} style={{ flex: 1 }}>
                    {mention.mentionQuery !== null && mention.filteredUsers.length > 0 && (
                      <Box className={s.mentionDropdown} ref={mention.dropdownRef}>
                        {mention.filteredUsers.map((u, idx) => {
                          const full = `${u.firstname ?? ""} ${u.name ?? ""}`.trim();
                          const initials = [u.firstname?.[0], u.name?.[0]].filter(Boolean).join("").toUpperCase();
                          return (
                            <Box key={u.userId ?? idx} className={`${s.mentionItem} ${idx === mention.mentionIndex ? s.mentionItemActive : ""}`} onMouseDown={(e) => { e.preventDefault(); mention.insertMention(u); }}>
                              <Box className={s.mentionAvatar}>{initials}</Box>
                              <Typography className={s.mentionName}>{full}</Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                    <textarea ref={mention.textareaRef} className={s.noteTextarea} style={{ width: "100%" }} placeholder={i18next.t("light.detail.addNote")} value={noteText} onChange={mention.handleTextChange} onKeyDown={(e) => mention.handleKeyDown(e, handleAddNote)} rows={2} />
                  </Box>
                  <button className={s.noteSendBtn} onClick={handleAddNote} disabled={!noteText.trim() || savingNote}>
                    <SendIcon style={{ fontSize: "14px" }} />
                  </button>
                </Box>}
                {notes.length > 0 && (
                  <Box style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                    <LightNotesList notes={notes} onDelete={handleDeleteNote} canDelete={!readOnly} classes={s} />
                  </Box>
                )}
              </Box>
            </>
          )}
        </Box>
      </Collapse>
    </Box>

    <LightDocViewer
      open={Boolean(viewerDoc)}
      onClose={() => { setViewerDoc(null); loadDetail(true, true); onStopped?.({ silent: true }); }}
      reponseId={viewerDoc?.reponseId}
      fileId={viewerDoc?.fileId}
      dossierId={dossier.dossierId}
      thirdpartyId={dossier.thirdpartyId}
      docName={viewerDoc?.documentName}
      typeDocumentId={viewerDoc?.typeDocumentId}
      acquisition={viewerDoc?.acquisition}
      evidences={viewerDoc?.evidences}
      onStatusChanged={() => { loadDetail(true, true); onStopped?.({ silent: true }); }}
    />

    <Dialog open={editAttrsOpen} onClose={() => setEditAttrsOpen(false)} maxWidth="sm" fullWidth scroll="paper" PaperProps={{ sx: { borderRadius: RADIUS.lg, fontFamily: FONT.family, maxHeight: "85vh" } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontFamily: "Inter", fontSize: "1rem", fontWeight: 700, color: BRAND.white, backgroundColor: BRAND.primary, pb: 1.5, flexShrink: 0 }}>
        <Box style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <FolderOutlinedIcon style={{ fontSize: "18px", color: BRAND.secondary }} />
          {i18next.t("light.detail.attributes")} — {dossier.dossierTypeLibelle || i18next.t("light.common.freeEvaluation")}
        </Box>
        <IconButton size="small" onClick={() => setEditAttrsOpen(false)} sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: BRAND.white } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 1, pb: 3, overflowY: "auto" }}>
        <LightEditableAttributes accountId={accountId} entityId={dossier.dossierId} bondType="dossier" onSaved={() => {
          getDossierAttributes(accountId, dossier.dossierId, null).then((r) => setDossierAttrs((r?.content ?? []).filter((a) => a.values?.length > 0))).catch(() => {});
        }} />
      </DialogContent>
    </Dialog>

    <StopConfirmDialog
      open={stopDialogOpen}
      onClose={() => { setStopDialogOpen(false); setStopReason(""); }}
      onConfirm={handleStopDossier}
      loading={stopping}
      title={i18next.t("light.detail.stopEvaluation")}
      reasonLabel={i18next.t("light.detail.stopReasonLabel")}
      reasonPlaceholder={i18next.t("light.detail.stopReasonPlaceholder")}
      reason={stopReason}
      onReasonChange={setStopReason}
      cancelLabel={i18next.t("cancel")}
      confirmLabel={i18next.t("light.detail.stopBtn")}
      confirmingLabel={i18next.t("light.detail.stopping")}
    >
      {i18next.t("light.detail.stopConfirm")} <strong>{dossier.dossierTypeLibelle || i18next.t("light.common.freeEvaluation")}</strong> ? {i18next.t("light.detail.stopReversible")}
    </StopConfirmDialog>

    <Dialog open={addDocOpen} onClose={() => setAddDocOpen(false)} maxWidth="sm" fullWidth scroll="paper" PaperProps={{ sx: { borderRadius: RADIUS.lg, fontFamily: FONT.family, maxHeight: "85vh" } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontFamily: "Inter", fontSize: "1rem", fontWeight: 700, color: BRAND.white, backgroundColor: BRAND.primary, pb: 1.5, flexShrink: 0 }}>
        <Box style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <ArticleOutlinedIcon style={{ fontSize: "18px", color: BRAND.secondary }} />
          {i18next.t("light.detail.manageDocuments")}
        </Box>
        <IconButton size="small" onClick={() => setAddDocOpen(false)} sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: BRAND.white } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2, pb: 3, overflowY: "auto" }}>
        {accountReqs === null ? (
          <Box style={{ display: "flex", justifyContent: "center", padding: "2rem" }}>
            <LightLoader size="sm" label={i18next.t("light.detail.loadingDocs")} />
          </Box>
        ) : (() => {
          const allDocs = accountReqs.filter((r) => !isSealedDocument(r.code));
          const filtered = docFilter.trim()
            ? allDocs.filter((r) => (r.descriptionFr || r.name || r.code || "").toLowerCase().includes(docFilter.trim().toLowerCase()))
            : allDocs;
          const sorted = [...filtered].sort((a, b) => {
            const aSelected = selectedNewDocs.includes(a.code) ? 0 : 1;
            const bSelected = selectedNewDocs.includes(b.code) ? 0 : 1;
            if (aSelected !== bSelected) return aSelected - bSelected;
            return (a.descriptionFr || a.name || a.code).localeCompare(b.descriptionFr || b.name || b.code);
          });
          return (
            <Box style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              <input className={light.fieldInput} placeholder={i18next.t("light.detail.searchDoc")} value={docFilter} onChange={(e) => setDocFilter(e.target.value)} autoFocus style={{ marginBottom: "0.25rem" }} />
              <Typography style={{ ...FONT.meta, color: BRAND.textMuted, fontFamily: FONT.family }}>
                {i18next.t("light.detail.selectedDocs", { count: selectedNewDocs.length })}
              </Typography>
              {filtered.length === 0 ? (
                <Box style={{ textAlign: "center", padding: "1rem", ...FONT.small, color: BRAND.textMuted, fontFamily: FONT.family, fontStyle: "italic" }}>
                  {i18next.t("light.detail.noDocMatch")}
                </Box>
              ) : sorted.map((req) => {
                const isSelected = selectedNewDocs.includes(req.code);
                return (
                  <Box key={req.code} onClick={() => setSelectedNewDocs((prev) => prev.includes(req.code) ? prev.filter((c) => c !== req.code) : [...prev, req.code])}
                    style={{ display: "flex", alignItems: "center", gap: "0.75rem", padding: "0.65rem 1rem", borderRadius: RADIUS.md, cursor: "pointer", border: `1.5px solid ${isSelected ? BRAND.primary : BRAND.border}`, backgroundColor: isSelected ? `${BRAND.primary}06` : BRAND.white, transition: "border-color 0.15s, background 0.15s" }}>
                    <Box style={{ width: "20px", height: "20px", borderRadius: "4px", flexShrink: 0, border: `2px solid ${isSelected ? BRAND.primary : BRAND.border}`, backgroundColor: isSelected ? BRAND.primary : BRAND.white, display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s" }}>
                      {isSelected && <span style={{ color: BRAND.white, fontSize: "12px", fontWeight: 700, lineHeight: 1 }}>✓</span>}
                    </Box>
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <Typography style={{ ...FONT.body, fontWeight: 600, color: BRAND.textMain, fontFamily: FONT.family }}>{getDoctypeTranslatedName(state.doctypeTraductions, req.code, state.lang) || req.descriptionFr || req.name || req.code}</Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          );
        })()}
      </DialogContent>
      <Box style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", padding: "0.75rem 1.5rem", borderTop: `1px solid ${BRAND.border}`, flexShrink: 0 }}>
        <button className={light.btnSecondary} style={{ padding: "0.5rem 1.25rem" }} onClick={() => setAddDocOpen(false)}>
          {i18next.t("cancel")}
        </button>
        <button className={light.btnPrimary} style={{ padding: "0.5rem 1.25rem" }} onClick={handleConfirmDocs} disabled={!(() => { const s = [...selectedNewDocs].sort(); const c = [...documents.map((d) => d.documentCode)].sort(); return s.length !== c.length || s.some((v, i) => v !== c[i]); })() || addingDocs}>
          {addingDocs ? i18next.t("light.detail.saving") : i18next.t("save")}
        </button>
      </Box>
    </Dialog>
    </>
  );
}
